import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, UserPlus, ArrowRightCircle, Gauge, Zap } from "lucide-react";
import QuickLeadDialog from "../components/QuickLeadDialog";
import AtecoInput from "../components/AtecoInput";
import CommittenteSelect, { CommittenteBadge } from "../components/CommittenteSelect";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";
import FormDialog from "../components/FormDialog";
import ConfirmDialog from "../components/ConfirmDialog";
import { FilterSelect } from "./Companies";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { LEAD_FIELDS, OPTIONS } from "../lib/entities";
import { useLookupOptions } from "../lib/lookups";
import { initials, label } from "../lib/format";

function ScoreRing({ score, breakdown }) {
  const color = score >= 70 ? "#059669" : score >= 40 ? "#d97706" : "#64748b";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="lead-score-ring" className="relative w-10 h-10" onClick={(e) => e.stopPropagation()}>
          <svg viewBox="0 0 36 36" className="w-10 h-10 -rotate-90">
            <circle cx="18" cy="18" r="15" fill="none" stroke="#e2e8f0" strokeWidth="3.5" />
            <circle cx="18" cy="18" r="15" fill="none" stroke={color} strokeWidth="3.5" strokeDasharray={`${(score / 100) * 94.2} 94.2`} strokeLinecap="round" />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold font-mono" style={{ color }}>{score}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72" onClick={(e) => e.stopPropagation()}>
        <p className="font-heading font-semibold text-sm mb-2 flex items-center gap-1.5"><Gauge className="w-4 h-4 text-teal-600" /> Composizione punteggio</p>
        <div className="space-y-2">
          {(breakdown || []).map((b) => (
            <div key={b.factor}>
              <div className="flex justify-between text-xs"><span className="text-slate-600">{b.factor}</span><span className="font-mono text-slate-800">{b.points}/{b.max}</span></div>
              <div className="h-1.5 rounded-full bg-slate-100 mt-0.5"><div className="h-full rounded-full bg-teal-500" style={{ width: `${(b.points / b.max) * 100}%` }} /></div>
              <p className="text-[10px] text-slate-400">{b.detail}</p>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Leads() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [source, setSource] = useState("all");
  const [ateco, setAteco] = useState("");
  const [sector, setSector] = useState("all");
  const [subsector, setSubsector] = useState("");
  const [committente, setCommittente] = useState("all");
  const sources = useLookupOptions("source"); const sectors = useLookupOptions("sector"); const subsectors = useLookupOptions("subsector");
  const [classification, setClassification] = useState("all");
  const [contactType, setContactType] = useState("all");
  const [edit, setEdit] = useState(null);
  const [create, setCreate] = useState(false);
  const [quick, setQuick] = useState(false);
  const [del, setDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (status !== "all") params.status = status;
    if (source !== "all") params.source = source;
    if (ateco) params.ateco = ateco;
    if (sector !== "all") params.sector = sector;
    if (subsector) params.subsector = subsector;
    if (committente !== "all") params.committente = committente;
    if (classification !== "all") params.classification = classification;
    if (contactType !== "all") params.contact_type = contactType;
    const { data } = await api.get("/leads", { params });
    setRows(data); setLoading(false);
  }, [search, status, source, ateco, sector, subsector, committente, classification, contactType]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("crm-refresh", h);
    return () => window.removeEventListener("crm-refresh", h);
  }, [load]);

  const convert = async (l) => {
    await api.post(`/leads/${l.id}/convert`, {});
    toast.success("Lead convertito in opportunità");
    load();
  };
  const remove = async () => { await api.delete(`/leads/${del.id}`); toast.success("Lead eliminato"); setDel(null); load(); };

  return (
    <div>
      <PageHeader title="Lead" subtitle={`${rows.length} lead · qualificazione e scoring`}>
        {can("write") && <Button data-testid="quick-lead-button" onClick={() => setQuick(true)} className="bg-teal-600 hover:bg-teal-700 text-white gap-1.5 shadow-md shadow-teal-600/20"><Zap className="w-4 h-4" /> Lead rapido</Button>}
        {can("write") && <Button data-testid="add-lead-button" variant="outline" onClick={() => setCreate(true)} className="gap-1.5"><Plus className="w-4 h-4" /> Nuovo Lead</Button>}
      </PageHeader>

      {can("write") && !loading && rows.length === 0 && (
        <button data-testid="quick-lead-empty-cta" onClick={() => setQuick(true)} className="w-full mb-4 rounded-xl border-2 border-dashed border-teal-300 bg-teal-50/50 hover:bg-teal-50 p-5 text-left flex items-center gap-4 transition-colors">
          <span className="w-10 h-10 rounded-full bg-teal-600 text-white flex items-center justify-center"><Zap className="w-5 h-5" /></span>
          <span><p className="font-heading font-semibold text-slate-800">Inserisci il primo lead in un solo passaggio</p><p className="text-sm text-slate-500">Azienda, persona e motivo dell'interesse in un unico form: niente duplicati, tutto collegato.</p></span>
        </button>
      )}

      <div className="flex flex-wrap gap-2.5 mb-4">
        <Input data-testid="lead-search" placeholder="Cerca…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-full sm:w-64 h-10" />
        <CommittenteSelect mode="filter" testid="filter-lead-committente" value={committente} onChange={setCommittente} />
        <FilterSelect testid="filter-lead-status" value={status} onChange={setStatus} placeholder="Stato" options={OPTIONS.lead_status} />
        <FilterSelect testid="filter-lead-source" value={source} onChange={setSource} placeholder="Fonte" options={sources} />
        <FilterSelect testid="filter-lead-classification" value={classification} onChange={setClassification} placeholder="Classif. azienda" options={OPTIONS.classification} />
        <FilterSelect testid="filter-lead-contact-type" value={contactType} onChange={setContactType} placeholder="Tipologia contatto" options={OPTIONS.contact_type} />
        <FilterSelect testid="filter-lead-sector" value={sector} onChange={setSector} placeholder="Settore" options={sectors} />
        <FilterSelect testid="filter-lead-subsector" value={subsector || "all"} onChange={(v) => setSubsector(v === "all" ? "" : v)} placeholder="Sottosettore" options={subsectors} />
        <AtecoInput testid="filter-lead-ateco" value={ateco} onChange={setAteco} placeholder="ATECO azienda" className="w-full sm:w-56" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden table-scroll">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wider text-slate-400">
              <th className="px-4 py-3 font-medium">Score</th>
              <th className="px-4 py-3 font-medium">Lead</th>
              <th className="px-4 py-3 font-medium">Settore</th>
              <th className="px-4 py-3 font-medium">Mercato</th>
              <th className="px-4 py-3 font-medium">Fonte</th>
              <th className="px-4 py-3 font-medium">Stato</th>
              <th className="px-4 py-3 font-medium text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Caricamento…</td></tr>
              : rows.length === 0 ? <tr><td colSpan={7} className="px-4 py-14 text-center text-slate-400"><UserPlus className="w-8 h-8 mx-auto mb-2 opacity-40" />Nessun lead</td></tr>
              : rows.map((l) => (
                <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="px-4 py-3"><ScoreRing score={l.score || 0} breakdown={l.score_breakdown} /></td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-800 flex items-center gap-2">{l.title}<CommittenteBadge value={l.committente} testid={`lead-committente-${l.id}`} /></p>
                    <p className="text-xs text-slate-400">{l.company_name || "—"}{l.company_classification && <span data-testid={`lead-classification-${l.id}`}> · {label("classification", l.company_classification)}</span>}{l.contact_type && <span data-testid={`lead-contact-type-${l.id}`}> · {label("contact_type", l.contact_type)}</span>}{l.company_ateco && <span data-testid={`lead-ateco-${l.id}`} title={l.company_ateco_label || ""}> · <span className="font-mono text-teal-700">{l.company_ateco}</span></span>}</p>
                  </td>
                  <td className="px-4 py-3 text-slate-600" data-testid={`lead-sector-${l.id}`}>
                    {l.company_sector || "—"}
                    {l.company_subsector && <p className="text-xs text-slate-500 mt-0.5">{l.company_subsector}</p>}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{l.market || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{l.source || "—"}</td>
                  <td className="px-4 py-3"><StatusBadge group="lead_status" value={l.status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {can("write") && l.status !== "convertito" && <button data-testid={`convert-lead-${l.id}`} onClick={() => convert(l)} className="icon-btn p-1.5 rounded-lg hover:bg-teal-50 text-teal-600" title="Converti in opportunità"><ArrowRightCircle className="w-4 h-4" /></button>}
                      {can("write") && <button data-testid={`edit-lead-${l.id}`} onClick={() => setEdit(l)} className="icon-btn p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Pencil className="w-4 h-4" /></button>}
                      {can("delete") && <button data-testid={`delete-lead-${l.id}`} onClick={() => setDel(l)} className="icon-btn p-1.5 rounded-lg hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <QuickLeadDialog open={quick} onOpenChange={setQuick} onSaved={load} />
      <FormDialog open={create} onOpenChange={setCreate} title="Nuovo Lead" fields={LEAD_FIELDS} endpoint="/leads" onSaved={load} />
      {edit && <FormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title="Modifica Lead" fields={LEAD_FIELDS} initial={edit} endpoint={`/leads/${edit.id}`} method="put" onSaved={load} />}
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} onConfirm={remove} description={`Eliminare "${del?.title}"?`} />
    </div>
  );
}
